import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, Crown } from 'lucide-react';
import { PRICE } from '@/constants/testIds/healthspan';
import { toast } from 'sonner';

export default function Pricing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get('/meta').then(r => setMeta(r.data)).catch(()=>{}); }, []);

  const startCheckout = async () => {
    if (!user) { nav('/login?next=/pricing'); return; }
    setBusy(true);
    try {
      const origin = window.location.origin;
      const { data } = await api.post('/billing/checkout', { origin_url: origin, package: 'researcher_monthly' });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Checkout failed');
      setBusy(false);
    }
  };

  const price = meta?.researcher_price_usd || 49;

  return (
    <div className="space-y-8">
      <div className="text-center max-w-2xl mx-auto">
        <div className="text-xs uppercase tracking-wider text-[color:var(--muted-fg)]">Pricing</div>
        <h1 className="mt-1 text-2xl sm:text-3xl font-semibold tracking-tight">Simple research tooling pricing</h1>
        <p className="mt-2 text-sm text-[color:var(--muted-fg)]">Stripe is configured in TEST mode in this environment. Use a Stripe test card (e.g., 4242 4242 4242 4242) to verify the flow.</p>
      </div>
      <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto">
        <Card data-testid={PRICE.freeCard} className="p-6 rounded-[var(--radius-md)] border border-[color:var(--border-color)] bg-[color:var(--surface)]">
          <div className="text-xs uppercase tracking-wider text-[color:var(--muted-fg)]">Free</div>
          <div className="mt-2 text-3xl font-semibold tracking-tight tabular-nums">$0<span className="text-sm font-normal text-[color:var(--muted-fg)]">/forever</span></div>
          <p className="mt-1 text-sm text-[color:var(--muted-fg)]">Public summaries and biomarker matching for informed laypeople.</p>
          <ul className="mt-5 space-y-2 text-sm">
            {['Biomarker matching','Public summaries','Source linking','Clinical trial browser','First-visit disclaimer modal'].map(f => (
              <li key={f} className="flex items-center gap-2"><Check className="h-4 w-4 text-[color:var(--success)]" /> {f}</li>
            ))}
          </ul>
          <Button variant="outline" className="mt-6 w-full" onClick={()=>nav('/match')}>Use free tier</Button>
        </Card>
        <Card data-testid={PRICE.researcherCard} className="p-6 rounded-[var(--radius-md)] border-2 border-[color:rgba(212,175,55,0.55)] bg-[color:var(--surface)] relative">
          <span className="absolute -top-3 left-6 inline-flex items-center gap-1 rounded-full bg-[color:var(--accent-gold)] text-[color:var(--brand-primary)] px-3 py-0.5 text-[11px] font-semibold tracking-wide"><Crown className="h-3 w-3" /> Researcher</span>
          <div className="text-xs uppercase tracking-wider text-[color:var(--muted-fg)]">Researcher / clinician</div>
          <div className="mt-2 text-3xl font-semibold tracking-tight tabular-nums">${price.toFixed(2)}<span className="text-sm font-normal text-[color:var(--muted-fg)]">/month</span></div>
          <p className="mt-1 text-sm text-[color:var(--muted-fg)]">Everything in Free, plus full technical access.</p>
          <ul className="mt-5 space-y-2 text-sm">
            {['Full technical summaries','Researcher-only data tables','References section per protocol','CSV export of matches','Priority access to weight tuning insights'].map(f => (
              <li key={f} className="flex items-center gap-2"><Check className="h-4 w-4 text-[color:var(--success)]" /> {f}</li>
            ))}
          </ul>
          {user?.role === 'researcher' || user?.role === 'admin' ? (
            <Button className="mt-6 w-full bg-[color:var(--success)] text-white hover:opacity-90" disabled>You’re a {user.role}</Button>
          ) : (
            <Button data-testid={PRICE.upgradeButton} disabled={busy} onClick={startCheckout} className="mt-6 w-full bg-[color:var(--accent-gold)] text-[color:var(--brand-primary)] hover:opacity-90">
              {busy ? 'Redirecting…' : `Upgrade for $${price.toFixed(2)}/mo`}
            </Button>
          )}
          <p className="mt-2 text-[11px] text-[color:var(--muted-fg)]">Test mode: no real charges. Cancel anytime through Stripe customer portal (production).</p>
        </Card>
      </div>
    </div>
  );
}
